export default function JourneySection() {
  const steps = [
    {
      step: "01",
      title: "Complete your intake — free, from home",
      description:
        "A few simple questions about your body, your lifestyle, and what walking comfortably means to you. Takes about 5 minutes on your couch. Always free for amputees — no cost, no catch.",
      icon: "ri-home-line",
      image:
        "https://readdy.ai/api/search-image?query=Close%20up%20shot%20of%20hands%20holding%20a%20tablet%20on%20a%20cozy%20sofa%2C%20soft%20morning%20sunlight%20streaming%20through%20windows%2C%20warm%20throw%20blanket%20visible%2C%20steaming%20coffee%20mug%20on%20wooden%20side%20table%20nearby%2C%20cream%20and%20beige%20living%20room%20interior%2C%20peaceful%20relaxed%20atmosphere%2C%20lifestyle%20photography%2C%20warm%20natural%20tones%2C%20shallow%20depth%20of%20field%20focusing%20on%20the%20tablet%20screen%20showing%20a%20welcoming%20interface%2C%20no%20faces%20visible&width=800&height=600&seq=journey-step1-v4&orientation=landscape",
    },
    {
      step: "02",
      title: "We compile your profile, pull your coverage",
      description:
        "Our system transforms your responses into the medical-necessary language physicians and prosthetists require. It pulls your insurance coverage and arranges financing if needed — all automatically, all for free.",
      icon: "ri-brain-line",
      image:
        "https://readdy.ai/api/search-image?query=Overhead%20flat%20lay%20of%20insurance%20documents%20and%20medical%20forms%20neatly%20arranged%20on%20a%20warm%20wooden%20desk%2C%20a%20steaming%20coffee%20mug%20beside%20the%20paperwork%2C%20soft%20natural%20window%20light%20casting%20gentle%20shadows%20across%20organized%20forms%2C%20a%20pen%20resting%20on%20top%20of%20a%20highlighted%20coverage%20document%2C%20warm%20beige%20and%20cream%20tones%20throughout%2C%20cozy%20organized%20home%20office%20atmosphere%2C%20sense%20of%20things%20being%20handled%20and%20taken%20care%20of%2C%20calm%20reassuring%20mood%2C%20no%20faces%20visible&width=800&height=600&seq=journey-step2-paperwork&orientation=landscape",
    },
    {
      step: "03",
      title: "Find a prosthetist or connect with yours",
      description:
        "First time? We match you with a great prosthetist in your area. Already have one you love? We connect directly with them. Either way, your complete file, insurance details, and our recommendations are ready before you walk in the door.",
      icon: "ri-hand-heart-line",
      image:
        "https://readdy.ai/api/search-image?query=Warm%20professional%20medical%20consultation%20close%20up%2C%20two%20people%20sitting%20across%20from%20each%20other%20in%20a%20bright%20modern%20clinic%20room%2C%20friendly%20engaged%20conversation%2C%20natural%20daylight%20through%20windows%2C%20plants%20visible%2C%20focus%20on%20warm%20genuine%20smiles%20and%20eye%20contact%2C%20supportive%20caring%20atmosphere%2C%20healthcare%20with%20humanity%2C%20warm%20beige%20and%20cream%20tones%2C%20professional%20yet%20approachable%2C%20upper%20body%20portrait%20framing&width=800&height=600&seq=journey-step2-v4&orientation=landscape",
    },
    {
      step: "04",
      title: "Your care team is aligned. You walk comfortably.",
      description:
        "Physician notes, prosthetist timelines, insurance approvals — everything is connected and streamlined automatically. The administrative delays that normally drag out the process? Gone. You get to the right fit faster — and it didn't cost you anything.",
      icon: "ri-walk-line",
      image:
        "https://static.readdy.ai/image/285054d16fa843ece955e8601c010093/4cf122e5488abaa0e823cdd4fe4f6b70.jpeg",
    },
  ];

  return (
    <section
      id="how-it-works"
      className="relative bg-background-100 py-20 md:py-28"
    >
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/3 -left-20 w-[350px] h-[350px] rounded-full bg-primary-100/25 blur-3xl"></div>
        <div className="absolute bottom-1/4 -right-16 w-[300px] h-[300px] rounded-full bg-accent-100/20 blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-10">
        <div className="text-center max-w-[680px] mx-auto mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-secondary-50 text-secondary-800 text-xs uppercase tracking-wider font-semibold mb-6">
            Your journey, simplified and free
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight">
            From your couch to
            <br />
            <span className="italic font-serif text-primary-600">
              walking comfortably
            </span>{" "}
            —
            <br />
            We handle everything in between
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-foreground-500 leading-relaxed max-w-[560px] mx-auto">
            The paperwork, the insurance, the financing, the back-and-forth
            between physician and prosthetist — that's where most delays live.
            Our system takes all of it off your plate. Free. Here's how it
            works.
          </p>
        </div>

        <div className="flex flex-col gap-8 md:gap-12">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`flex flex-col ${index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"} gap-8 lg:gap-14 items-center`}
            >
              <div className="flex-1 w-full">
                <div className="relative rounded-2xl overflow-hidden group cursor-default">
                  <img
                    src={step.image}
                    alt={step.title}
                    className="w-full aspect-[4/3] object-cover rounded-2xl group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent rounded-2xl"></div>
                </div>
              </div>

              <div className="flex-1 w-full lg:max-w-[440px]">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-accent-50 text-accent-600 flex-shrink-0">
                    <i className={`${step.icon} text-2xl`}></i>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-heading text-4xl md:text-5xl font-bold text-foreground-200 tracking-tight">
                      {step.step}
                    </span>
                  </div>
                </div>

                <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-3">
                  {step.title}
                </h3>
                <p className="text-[15px] text-foreground-500 leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-3 px-6 py-4 rounded-2xl bg-background-50 border border-background-200/70">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-50 flex-shrink-0">
              <i className="ri-checkbox-circle-line text-accent-600 text-lg"></i>
            </div>
            <p className="text-sm text-foreground-600 text-left leading-relaxed">
              <span className="font-semibold text-foreground-950">
                Completely free for amputees.
              </span>{" "}
              No cost, no catch, no insurance claim required to get started. We
              handle the admin so you and your care team can focus on the fit.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
